import {
  zMeResponse,
  zSessionResponse,
  zAttemptBatchResponse,
  zWalletResponse,
  zProgressResponse,
  zMistakesResponse,
  zHintResponse,
  zPlayWindowResponse,
  zFigureFailuresResponse,
  zReviewQueueItem,
  type FigureFailuresResponse,
  type AttemptInput,
  type MeResponse,
  type SessionResponse,
  type StartSessionInput,
  type ProgressResponse,
  type DifficultyMode,
  type SubjectId,
  type SessionMode,
  type Grade,
  type ScheduleWindow,
} from '@lk/shared';
import { apiRequest } from './http';
import { getDeviceId } from './device';
import { newIdempotencyKey } from './ids';

/**
 * Типи виводимо просто з контрактних схем через `ReturnType<...parse>` —
 * без прямого імпорту zod у застосунок. Так у вебі не може зʼявитися
 * друга версія бібліотеки валідації, несумісна з тією, що в @lk/shared.
 */
export type AttemptBatchResponse = ReturnType<typeof zAttemptBatchResponse.parse>;
export type WalletResponse = ReturnType<typeof zWalletResponse.parse>;
export type MistakesResponse = ReturnType<typeof zMistakesResponse.parse>;
export type HintResponse = ReturnType<typeof zHintResponse.parse>;
export type PlayWindowResponse = ReturnType<typeof zPlayWindowResponse.parse>;
export type ReviewQueueItem = ReturnType<typeof zReviewQueueItem.parse>;

/**
 * Шляхи API. Звірені з apps/api/src/routes/*.ts — саме тут два боки
 * сходяться, тому будь-яка зміна маршруту на сервері має відбитися тут
 * і ніде більше.
 *
 * Ресурси дитини адресуються параметром шляху (`/wallet/<id>`), а не
 * запитом: так сервер бачить власника ресурсу до розбору тіла запиту
 * і може перевірити право доступу однаково для всіх методів.
 */
export const ENDPOINTS = {
  me: '/me',
  loginParent: '/auth/login/parent',
  logout: '/auth/logout',
  loginPin: '/auth/login/pin',
  sessions: '/sessions',
  attempts: '/attempts',
  hint: '/hints',
  wallet: (learnerId: string) => `/wallet/${encodeURIComponent(learnerId)}`,
  payout: '/wallet/payout',
  withdraw: '/wallet/withdraw',
  progress: (learnerId: string) => `/progress/${encodeURIComponent(learnerId)}`,
  playWindow: (learnerId: string) => `/play-window/${encodeURIComponent(learnerId)}`,
  mistakes: (learnerId: string) => `/mistakes/${encodeURIComponent(learnerId)}`,
  controls: (learnerId: string) => `/controls/${encodeURIComponent(learnerId)}`,
  reviewQueue: '/review-queue',
  figureFailures: '/figure-failures',
  reviewDecision: '/review-queue/decision',
  passkeyRegisterOptions: '/auth/webauthn/register/options',
  passkeyRegisterVerify: '/auth/webauthn/register/verify',
  passkeyLoginOptions: '/auth/webauthn/login/options',
  passkeyLoginVerify: '/auth/webauthn/login/verify',
  passkeys: '/auth/webauthn/passkeys',
  passkey: (id: string) => `/auth/webauthn/passkeys/${encodeURIComponent(id)}`,
} as const;

// ─────────────────────────────── Авторизація ───────────────────────────────

export function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return apiRequest(ENDPOINTS.me, {
    schema: zMeResponse,
    silentUnauthorized: true,
    ...(signal ? { signal } : {}),
  });
}

/**
 * Єдиний вхід: лише PIN. Кому він належить — визначає сервер,
 * тому клієнт узагалі не знає списку профілів до автентифікації.
 */
export function loginPin(input: { pin: string }): Promise<MeResponse> {
  return apiRequest(ENDPOINTS.loginPin, {
    method: 'POST',
    body: { pin: input.pin, deviceId: getDeviceId() },
    schema: zMeResponse,
    silentUnauthorized: true,
  });
}

export function loginParent(input: { email: string; password: string }): Promise<MeResponse> {
  return apiRequest(ENDPOINTS.loginParent, {
    method: 'POST',
    body: { ...input, deviceId: getDeviceId() },
    schema: zMeResponse,
    silentUnauthorized: true,
  });
}

export function logout(): Promise<void> {
  return apiRequest(ENDPOINTS.logout, { method: 'POST' });
}

// ───────────────────────────────── Сесія ─────────────────────────────────

export interface StartSessionArgs {
  learnerId: string;
  mode?: SessionMode;
  difficultyMode: DifficultyMode;
  length: number;
  subject?: SubjectId;
  /** Передається явно, щоб повтор запиту після обриву не створив другу сесію. */
  idempotencyKey?: string;
}

export function startSession(args: StartSessionArgs): Promise<SessionResponse> {
  const payload: StartSessionInput = {
    learnerId: args.learnerId,
    mode: args.mode ?? 'daily',
    difficultyMode: args.difficultyMode,
    length: args.length,
    idempotencyKey: args.idempotencyKey ?? newIdempotencyKey(),
    ...(args.subject ? { subject: args.subject } : {}),
  };
  return apiRequest(ENDPOINTS.sessions, {
    method: 'POST',
    body: payload,
    schema: zSessionResponse,
  });
}

/** Чи можна зараз грати і коли почнеться (чи скінчиться) перерва. */
export function fetchPlayWindow(learnerId: string): Promise<PlayWindowResponse> {
  return apiRequest(ENDPOINTS.playWindow(learnerId), { schema: zPlayWindowResponse });
}

/**
 * Єдина точка відправки спроб. Викликається ТІЛЬКИ синхронізатором черги —
 * жоден екран не шле спроби напряму, інакше зʼявиться шлях, на якому
 * відповідь може загубитися.
 */
export function postAttempts(attempts: AttemptInput[]): Promise<AttemptBatchResponse> {
  return apiRequest(ENDPOINTS.attempts, {
    method: 'POST',
    body: { attempts },
    schema: zAttemptBatchResponse,
  });
}

export function fetchHint(input: {
  sessionId: string;
  itemId: string;
  index: number;
}): Promise<HintResponse> {
  return apiRequest(ENDPOINTS.hint, { method: 'POST', body: input, schema: zHintResponse });
}

// ──────────────────────────────── Гаманець ────────────────────────────────

export function fetchWallet(learnerId: string, signal?: AbortSignal): Promise<WalletResponse> {
  return apiRequest(ENDPOINTS.wallet(learnerId), {
    schema: zWalletResponse,
    ...(signal ? { signal } : {}),
  });
}

export function payout(input: {
  learnerId: string;
  amount: number;
  note?: string;
}): Promise<WalletResponse> {
  return apiRequest(ENDPOINTS.payout, {
    method: 'POST',
    body: { ...input, idempotencyKey: newIdempotencyKey() },
    schema: zWalletResponse,
  });
}

/**
 * Вивід балів, який запускає сама дитина.
 * Для «всі доступні» суму НЕ передаємо: її рахує сервер у момент запису,
 * інакше застаріле число з екрана могло б розійтися з реальним балансом.
 */
export function withdrawCoins(input: {
  learnerId: string;
  request: { mode: 'all' } | { mode: 'exact'; amount: number };
}): Promise<WalletResponse> {
  return apiRequest(ENDPOINTS.withdraw, {
    method: 'POST',
    body: { ...input, idempotencyKey: newIdempotencyKey() },
    schema: zWalletResponse,
  });
}

// ───────────────────────── Батьківський кабінет ─────────────────────────

export function fetchProgress(
  learnerId: string,
  days = 30,
  signal?: AbortSignal,
): Promise<ProgressResponse> {
  return apiRequest(ENDPOINTS.progress(learnerId), {
    schema: zProgressResponse,
    query: { days },
    ...(signal ? { signal } : {}),
  });
}

export function fetchMistakes(learnerId: string, signal?: AbortSignal): Promise<MistakesResponse> {
  return apiRequest(ENDPOINTS.mistakes(learnerId), {
    schema: zMistakesResponse,
    ...(signal ? { signal } : {}),
  });
}

export interface ControlsPayload {
  learnerId: string;
  schedule: ScheduleWindow[];
  dailyMinutesLimit: number;
  sessionMinutes: number;
  breakMinutes: number;
  dailyGoalItems: number;
  dailyCoinCap: number;
  enabledSubjects: SubjectId[];
  subjectWeightOverride?: Partial<Record<SubjectId, number>>;
  grade?: Grade;
  targetSuccess?: number;
}

export function fetchControls(
  learnerId: string,
  signal?: AbortSignal,
): Promise<ControlsPayload> {
  return apiRequest<ControlsPayload>(ENDPOINTS.controls(learnerId), {
    ...(signal ? { signal } : {}),
  });
}

export function saveControls(payload: ControlsPayload): Promise<ControlsPayload> {
  return apiRequest<ControlsPayload>(ENDPOINTS.controls(payload.learnerId), {
    method: 'PUT',
    body: payload,
  });
}

const reviewQueueSchema = {
  parse(value: unknown): { items: ReviewQueueItem[] } {
    const raw = (value as { items?: unknown })?.items;
    if (!Array.isArray(raw)) throw new Error('Очікували масив питань на модерацію');
    return { items: raw.map((entry) => zReviewQueueItem.parse(entry)) };
  },
};

export async function fetchReviewQueue(signal?: AbortSignal): Promise<ReviewQueueItem[]> {
  const data = await apiRequest(ENDPOINTS.reviewQueue, {
    schema: reviewQueueSchema,
    ...(signal ? { signal } : {}),
  });
  return data.items;
}

/**
 * Малюнки, які не відмалювалися на екрані дитини.
 *
 * Зворотний бік тиші `SafeSvg`: він на зламаній розмітці повертає `null`, і
 * без цього списку про порожню рамку не дізнався б ніхто.
 */
export async function fetchFigureFailures(
  signal?: AbortSignal,
): Promise<FigureFailuresResponse['items']> {
  const data = await apiRequest(ENDPOINTS.figureFailures, {
    schema: zFigureFailuresResponse,
    ...(signal ? { signal } : {}),
  });
  return data.items;
}

export function submitReviewDecision(input: {
  itemId: string;
  decision: 'approve' | 'reject' | 'edit';
  patch?: {
    prompt?: string;
    choices?: Array<{ id: string; text: string }>;
    explanation?: string;
    difficulty?: number;
  };
  reason?: string;
}): Promise<void> {
  return apiRequest(ENDPOINTS.reviewDecision, { method: 'POST', body: input });
}
